/* global ChoiceOption */
import _ from 'lodash';
import {
  Column, Element, FilterChip, FilterParam, Client,
  IssueColumn, TaskColumn, SystemTypeEnum,
} from '../column-filter.types';
import { utcDateTimeFields } from './column-filter-maps';
import {
  dateFilter, noFilterList, storageKey, systemTypeMap,
  fieldNameMap, sqlMap, columnMap, filterKeyMap,
  DatePickers, StaticDateValues,
} from '.';
import DateF from '../../../../models/date-f';

function isFilterable(column: Column) {
  return !noFilterList.includes(column.accessor as string);
}

export function getQueryOptions(column: string): { [key: string]: string } | undefined {
  return utcDateTimeFields.includes(column) ? { time_zone: 'Etc/UTC' } : undefined;
}

function findPriorityField(elements: Element[], key: string): Element {
  let fieldType;

  elements.some(function recurs(element: Element) {
    if (element.key === key) {
      fieldType = element;
      return true;
    }
    // @ts-ignore
    return element.elements && element.elements.some(recurs);
  });

  return fieldType;
}

export function getFilteredSelected(selected: ChoiceOption[], values: ChoiceOption[]) {
  const valuesArr = values.map((o) => o.value);
  return selected.filter((s) => valuesArr.includes(s.value));
}

export function setChecked(selected: ChoiceOption[], value: string) {
  if (!selected.length) {
    return false;
  }

  return !!selected.find((option: ChoiceOption) => option.value === value);
}

export function createAChip(
  cols: any,
  options: ChoiceOption[],
  filterCategory?: string,
): FilterChip {
  const columnLabel = cols.filter((col: Column) => col.accessor === filterCategory)[0];
  let displayValues = options.map((option) => option.value).join(', ');

  // formats the date for chips
  if (DatePickers.includes(columnLabel.accessor) && !StaticDateValues.includes(displayValues)) {
    displayValues = !displayValues.length ? '' : displayValues.split(',')
      .map((option) => new DateF(option.trim()).toLocaleString())
      .map((item) => item.split(',')[0])
      .join(', ')
      .replace(',', ' to');
  }

  return {
    key: columnLabel.accessor,
    label: `${columnLabel.header}: ${displayValues}`,
    field: columnLabel.header,
    values: options,
  };
}

/** Replaces existing chip with updated values */
export function createChips(
  chips: FilterChip[],
  chip: FilterChip,
  category: string,
): FilterChip[] {
  const chipIndex = chips.findIndex((col) => col.key === category);
  const replacedChip = chips.splice(chipIndex, 1, chip);
  return _.uniqBy([...chips, ...replacedChip], 'key');
}

/** Creates a new chip by the associated column accessor */
export function getNewChips(
  cols: {accessor: string, header: string}[],
  colChips: FilterChip[],
  filterCategory?: string,
) {
  const label = cols.filter((col) => col.accessor === filterCategory)[0];

  const chip: FilterChip = {
    key: label.accessor,
    label: `${label.header}: `,
    field: label.header,
    values: [],
  };

  if (_.some(colChips, (c: FilterChip) => c.key === filterCategory)) {
    return colChips;
  }

  return [...colChips, chip];
}

export function setAnchorForChip(
  chips: FilterChip[],
  columnDataChips: FilterChip[],
  anchor: Element,
) {
  if (_.isEmpty(_.xorWith(chips, columnDataChips, _.isEqual))) {
    return anchor;
  }

  return null;
}

export function getSelectedValues(category: string, colChips: FilterChip[]) {
  const selected = colChips.length && colChips.filter((col) => col.key === category)[0];
  return selected ? selected.values : [];
}

export function deserializeColumns(cols: IssueColumn[] | TaskColumn[]) {
  const filterable = cols.filter(isFilterable);
  const deserializedCols = filterable.map(({ Header, accessor }) => ({
    header: Header,
    accessor: columnMap[accessor as string],
  }));

  deserializedCols.unshift(dateFilter);
  return deserializedCols;
}

const isCustomDateRangeFromParams = (key: string, values: string) => {
  if (DatePickers.includes(key) && !StaticDateValues.includes(values)) {
    return !values.length ? '' : values.replace(/'/g, '').split(',')
      .every((option) => new DateF(option.trim()).isDate());
  }
  return false;
};

export const convertToOptions = async (
  api: Client, key: string, id: string, type: SystemTypeEnum, values: string,
) => {
  const queryKey = filterKeyMap[type][key];
  const sql = sqlMap(queryKey, id, type);
  if (!values.length) {
    return [];
  }
  if (sql) {
    // fetch values selected for key and return options array
    const resValues: ChoiceOption[] = await api.query.queryRecords({ q: sql });
    return resValues.filter((option) => values.split('\'').includes(option.value));
  }
  // rebuilds custom date range from params
  if (isCustomDateRangeFromParams(key, values)) {
    return [{
      label: 'Custom',
      value: values.replace(/'/g, ''),
    }];
  }
  // convert into options array
  return values.replace(/'/g, '').split(',').map((item) => ({
    label: item,
    value: item,
  }));
};

export async function generateFiltersFromParamsObject(
  api: Client, id: string, type: SystemTypeEnum, queryObject: any,
): Promise<FilterChip[]> {
  return Promise.all(Object.keys(queryObject).map(async (key) => ({
    key,
    field: fieldNameMap[key],
    label: `${fieldNameMap[key]}: ${queryObject[key]}`,
    values: await convertToOptions(api, key, id, type, queryObject[key]),
  })));
}

export function persistToLocalStorage(type: SystemTypeEnum, chips: FilterChip[]) {
  return localStorage.setItem(`${type}-${storageKey}`, JSON.stringify(chips));
}

export function formatFiltersForQuery(chips: FilterChip[], type?: SystemTypeEnum): FilterParam[] {
  return chips.map((c) => ({
    key: filterKeyMap[type as SystemTypeEnum][c.key],
    field: c.field,
    values: (c.values as ChoiceOption[]).map((opt) => opt.value),
    options: getQueryOptions(c.key),
  }));
}

export function getPriorities(form: any) {
  const field = form && findPriorityField(form.elements, systemTypeMap[form.system_type].value);
  return field.choices;
}
